import { useMemo, useState } from 'react';
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useMappableProfileFields } from '@/hooks/use-mappable-profile-fields';
import type { MappableProfileField } from '@hygge/shared';

interface MappingFieldSelectProps {
  value: string | null;
  onChange: (key: string, field: MappableProfileField) => void;
}

// Searchable picker over the HW-563 profile-field catalog, grouped by the
// field's breadcrumb (e.g. "Personal Information" or "Job → Bank Details").
export function MappingFieldSelect({ value, onChange }: MappingFieldSelectProps) {
  const [open, setOpen] = useState(false);
  const { data: fields, isLoading } = useMappableProfileFields();

  const groups = useMemo(() => {
    const byGroup = new Map<string, MappableProfileField[]>();
    for (const field of fields ?? []) {
      const label = field.breadcrumb.join(' → ') || 'Other';
      const list = byGroup.get(label) ?? [];
      list.push(field);
      byGroup.set(label, list);
    }
    return [...byGroup.entries()];
  }, [fields]);

  const selected = useMemo(
    () => (fields ?? []).find((f) => f.key === value) ?? null,
    [fields, value],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
        >
          <span className={cn('truncate', !selected && 'text-muted-foreground')}>
            {selected ? selected.label : 'Select a profile field…'}
          </span>
          {isLoading ? (
            <Loader2 className="ml-2 h-4 w-4 shrink-0 animate-spin opacity-50" />
          ) : (
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search profile fields…" />
          <CommandList>
            <CommandEmpty>No matching fields.</CommandEmpty>
            {groups.map(([group, groupFields]) => (
              <CommandGroup key={group} heading={group}>
                {groupFields.map((field) => (
                  <CommandItem
                    key={field.key}
                    value={`${field.label} ${group}`}
                    onSelect={() => {
                      onChange(field.key, field);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        field.key === value ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    <div className="flex flex-col">
                      <span>{field.label}</span>
                      <span className="text-[11px] text-muted-foreground">
                        {field.source === 'SYSTEM' ? 'System field' : 'Custom field'} · {field.type}
                      </span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
